import { Component, type OnInit, inject, signal, computed, HostListener, PLATFORM_ID } from '@angular/core';
import { CommonModule , isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SawdustDevToolsService } from './sawdust-devtools.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LogLevel } from '@cudirect/sawdust/types';

@Component({
  selector: 'app-sawdust-devtools',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (devtools.getIsOpen()) {
    <div
      class="sawdust-devtools"
      [class.minimized]="devtools.getIsMinimized()"
      [style.left.px]="devtools.getPosition().x"
      [style.top.px]="devtools.getPosition().y"
      (mousedown)="startDrag($event)"
    >
      <!-- Header -->
      <div class="sawdust-devtools-header">
        <div class="sawdust-devtools-title">
          <span>🪵 SawDust DevTools</span>
        </div>
        <div class="sawdust-devtools-actions">
          <button (click)="devtools.toggleMinimize()" class="sawdust-btn">
            {{ devtools.getIsMinimized() ? 'Expand' : 'Minimize' }}
          </button>
          <button (click)="devtools.toggleOpen()" class="sawdust-btn sawdust-btn-close">Close</button>
        </div>
      </div>
      <!-- Content (hidden when minimized) -->
      @if (!devtools.getIsMinimized()) {
      <div class="sawdust-devtools-content">
        <!-- Toolbar -->
        <div class="sawdust-devtools-toolbar">
          <div class="sawdust-devtools-filters">
            @for (level of logLevels; track level) {
            <button (click)="devtools.setFilter(level.value)" class="sawdust-btn" [class.active]="activeFilter() === level.value">
              {{ level.label }}
            </button>
            }
          </div>
          <div class="sawdust-devtools-search">
            <input
              type="text"
              placeholder="Search logs..."
              [ngModel]="searchTerm()"
              (ngModelChange)="devtools.setSearchTerm($event)"
              class="sawdust-input"
            />
          </div>
          <div class="sawdust-devtools-actions">
            <button (click)="devtools.clearLogs()" class="sawdust-btn">Clear</button>
            <button (click)="devtools.exportLogs()" class="sawdust-btn">Export</button>
            <select [ngModel]="logger.getLogLevel()" (ngModelChange)="setLogLevel($event)" class="sawdust-select">
              <option [ngValue]="LogLevel.debug">Debug ({{ LogLevel.debug }})</option>
              <option [ngValue]="LogLevel.info">Info ({{ LogLevel.info }})</option>
              <option [ngValue]="LogLevel.warn">Warn ({{ LogLevel.warn }})</option>
              <option [ngValue]="LogLevel.error">Error ({{ LogLevel.error }})</option>
            </select>
          </div>
        </div>
        <!-- Logs -->
        <div class="sawdust-devtools-logs">
          @if (filteredLogs().length === 0) {
          <div class="sawdust-devtools-empty">No logs to display</div>
          } @for (log of filteredLogs(); track log) {
          <div class="sawdust-log-entry" [class]="'sawdust-log-' + log.level">
            <div class="sawdust-log-header">
              <span class="sawdust-log-timestamp">{{ formatTime(log.timestamp) }}</span>
              <span class="sawdust-log-level">{{ log.level.toUpperCase() }}</span>
              <span class="sawdust-log-service">{{ log.name }}</span>
            </div>
            <div class="sawdust-log-message">{{ log.message }}</div>
            @if (hasDetails(log)) {
            <div class="sawdust-log-details">
              @if (objectKeys(log.context).length > 0) {
              <div class="sawdust-log-context">
                <strong>Context:</strong>
                <pre>{{ log.context | json }}</pre>
              </div>
              } @if (objectKeys(log.metadata).length > 0) {
              <div class="sawdust-log-metadata">
                <strong>Metadata:</strong>
                <pre>{{ log.metadata | json }}</pre>
              </div>
              } @if (log.error) {
              <div class="sawdust-log-error">
                <strong>Error:</strong>
                <pre>{{ formatError(log.error) }}</pre>
              </div>
              }
            </div>
            }
          </div>
          }
        </div>
      </div>
      }
    </div>
    }

    <!-- Floating button when closed -->
    @if (!devtools.getIsOpen()) {
    <button (click)="devtools.toggleOpen()" class="sawdust-devtools-button">🪵 SawDust</button>
    }
  `,
  styles: [
    `
      :host {
        position: fixed;
        z-index: 9999;
      }

      .sawdust-devtools {
        position: fixed;
        width: 500px;
        background-color: #ffffff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue',
          sans-serif;
        font-size: 14px;
        overflow: hidden;
        transition: height 0.3s ease;
        resize: both;
      }

      .sawdust-devtools.minimized {
        height: auto !important;
        width: 300px !important;
        resize: none;
      }

      .sawdust-devtools-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background-color: #f5f5f5;
        border-bottom: 1px solid #e0e0e0;
        cursor: move;
      }

      .sawdust-devtools-title {
        font-weight: 600;
        font-size: 16px;
      }

      .sawdust-devtools-actions {
        display: flex;
        gap: 8px;
      }

      .sawdust-devtools-content {
        height: calc(100% - 40px);
        display: flex;
        flex-direction: column;
      }

      .sawdust-devtools-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid #e0e0e0;
        background-color: #fafafa;
      }

      .sawdust-devtools-filters {
        display: flex;
        gap: 4px;
      }

      .sawdust-devtools-logs {
        flex: 1;
        overflow-y: auto;
        padding: 8px;
      }

      .sawdust-log-entry {
        margin-bottom: 8px;
        padding: 8px;
        border-radius: 4px;
        border-left: 4px solid #ccc;
        background-color: #f9f9f9;
      }

      .sawdust-log-debug {
        border-left-color: #9e9e9e;
      }

      .sawdust-log-info {
        border-left-color: #2196f3;
      }

      .sawdust-log-warn {
        border-left-color: #ff9800;
      }

      .sawdust-log-error {
        border-left-color: #f44336;
      }

      .sawdust-log-header {
        display: flex;
        gap: 8px;
        margin-bottom: 4px;
        font-size: 12px;
        color: #757575;
      }

      .sawdust-log-level {
        font-weight: 600;
      }

      .sawdust-log-message {
        font-family: monospace;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .sawdust-log-details {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px dashed #e0e0e0;
      }

      .sawdust-log-context,
      .sawdust-log-metadata,
      .sawdust-log-error {
        margin-top: 4px;
      }

      pre {
        margin: 4px 0;
        padding: 8px;
        background-color: #f0f0f0;
        border-radius: 4px;
        overflow-x: auto;
        font-size: 12px;
      }

      .sawdust-btn {
        padding: 4px 8px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        background-color: #ffffff;
        cursor: pointer;
        font-size: 12px;
      }

      .sawdust-btn:hover {
        background-color: #f5f5f5;
      }

      .sawdust-btn.active {
        background-color: #e0e0e0;
        font-weight: 600;
      }

      .sawdust-btn-close {
        color: #f44336;
      }

      .sawdust-input {
        padding: 4px 8px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 12px;
        width: 150px;
      }

      .sawdust-select {
        padding: 4px 8px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 12px;
      }

      .sawdust-devtools-empty {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100px;
        color: #9e9e9e;
        font-style: italic;
      }

      .sawdust-devtools-button {
        position: fixed;
        bottom: 20px;
        right: 20px;
        padding: 8px 16px;
        background-color: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .sawdust-devtools-button:hover {
        background-color: #1976d2;
      }
    `
  ]
})
export class SawdustDevToolsComponent implements OnInit {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  devtools = inject(SawdustDevToolsService);
  logger = inject(SawDust);

  LogLevel = LogLevel;
  objectKeys = Object.keys;

  // Signals
  activeFilter = signal<string>('all');
  searchTerm = signal<string>('');
  filteredLogs = computed(() => this.devtools.filteredLogs());

  // Dragging state
  private isDragging = false;
  private dragOffset = { x: 0, y: 0 };

  // Log levels for filter buttons
  logLevels = [
    { label: 'All', value: 'all' },
    { label: 'Debug', value: 'debug' },
    { label: 'Info', value: 'info' },
    { label: 'Warn', value: 'warn' },
    { label: 'Error', value: 'error' }
  ];

  ngOnInit(): void {
    // Initialize with some sample logs for demonstration
    this.addSampleLogs();
  }

  setLogLevel(level: LogLevel): void {
    this.logger.setLevel(level);
  }

  startDrag(event: MouseEvent): void {
    // Only start dragging from the header
    const target = event.target as HTMLElement;
    if (!target.closest('.sawdust-devtools-header')) return;

    this.isDragging = true;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };

    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const x = event.clientX - this.dragOffset.x;
    const y = event.clientY - this.dragOffset.y;

    this.devtools.setPosition(x, y);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  formatTime(timestamp: string): string {
    if (!this.isBrowser) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString() + '.' + date.getMilliseconds().toString().padStart(3, '0');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatError(error: any): string {
    if (!error) return 'No error details';

    if (typeof error === 'string') return error;

    let result = '';
    if (error.name) result += `${error.name}: `;
    if (error.message) result += error.message;
    if (error.stack) result += `\n${error.stack}`;

    return result || JSON.stringify(error, null, 2);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hasDetails(log: any): boolean {
    return Object.keys(log.context || {}).length > 0 || Object.keys(log.metadata || {}).length > 0 || !!log.error;
  }

  // Add some sample logs for demonstration
  private addSampleLogs(): void {
    const now = new Date().toISOString();

    this.devtools.addLog({
      id: '1',
      timestamp: now,
      level: 'info',
      message: 'Application started',
      context: { component: 'AppComponent' },
      metadata: { version: '1.0.0' },
      name: 'AppRoot',
      env: 'development'
    });

    this.devtools.addLog({
      id: '2',
      timestamp: now,
      level: 'debug',
      message: 'Config loaded',
      context: { component: 'ConfigService' },
      metadata: { config: { theme: 'light', language: 'en' } },
      name: 'AppRoot',
      env: 'development'
    });

    this.devtools.addLog({
      id: '3',
      timestamp: now,
      level: 'warn',
      message: 'Deprecated feature used',
      context: { component: 'FeatureService' },
      metadata: { feature: 'oldAPI', alternative: 'newAPI' },
      name: 'AppRoot',
      env: 'development'
    });

    this.devtools.addLog({
      id: '4',
      timestamp: now,
      level: 'error',
      message: 'Failed to load resource',
      context: { component: 'ResourceLoader' },
      metadata: { url: 'https://example.com/api/data' },
      error: {
        name: 'Error',
        message: 'Network request failed',
        stack: 'Error: Network request failed\n    at ResourceLoader.loadResource (resource-loader.ts:42)'
      },
      name: 'AppRoot',
      env: 'development'
    });
  }
}
